import React, { useEffect, useCallback, useState, useRef } from 'react';
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Button, Collapse, Input, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
// Import Data
import navdata from "../LayoutMenuData";
//i18n
import { withTranslation } from "react-i18next";
import withRouter from "../../Components/Common/withRouter";
import { useSelector } from "react-redux";
import { createSelector } from 'reselect';
import { APIClient } from "../../helpers/api_helper";

const PROJECTS_ACTION_ID = 'open-projects-modal';
const apiClient = new APIClient();

// Move selector outside component to maintain memoization
const selectLayoutState = (state : any) => state.Layout;
const selectLayoutProperties = createSelector(
    selectLayoutState,
    (layout) => ({
        leftsidbarSizeType: layout.leftsidbarSizeType,
        sidebarVisibilitytype: layout.sidebarVisibilitytype,
        layoutType: layout.layoutType
    })
);

interface ProjectItem {
    projectId: number;
    projectName: string;
    sortOrder: number;
}

const VerticalLayout = (props : any) => {
    const navDataResult = navdata();
    const navData = navDataResult.menuItems.props.children;
    const refreshProjects = navDataResult.refreshProjects;
    const path = props.router.location.pathname;

    // Manage Projects modal state
    const [isManageModalOpen, setIsManageModalOpen] = useState(false);
    const [manageProjects, setManageProjects] = useState<ProjectItem[]>([]);
    const [manageLoading, setManageLoading] = useState(false);
    const [manageError, setManageError] = useState('');

    // Inline edit
    const [editingProjectId, setEditingProjectId] = useState<number | null>(null);
    const [editingName, setEditingName] = useState('');
    const [editSaving, setEditSaving] = useState(false);

    // Delete confirmation (inline)
    const [deletingProjectId, setDeletingProjectId] = useState<number | null>(null);
    const [deleteConfirming, setDeleteConfirming] = useState(false);

    // Add new project
    const [newProjectName, setNewProjectName] = useState('');
    const [newProjectAdding, setNewProjectAdding] = useState(false);

    // Drag & drop
    const [dragOrderChanged, setDragOrderChanged] = useState(false);
    const [savingOrder, setSavingOrder] = useState(false);
    const dragItem = useRef<number | null>(null);
    const dragOverItem = useRef<number | null>(null);

    const {
        leftsidbarSizeType, sidebarVisibilitytype, layoutType
    } = useSelector(selectLayoutProperties);

    const loadProjects = async () => {
        setManageLoading(true);
        setManageError('');
        try {
            const response: any = await apiClient.get('/project');
            setManageProjects(response?.data ?? []);
        } catch (error) {
            setManageError('Failed to load projects.');
        } finally {
            setManageLoading(false);
        }
    };

    const openManageModal = () => {
        setIsManageModalOpen(true);
        setEditingProjectId(null);
        setDeletingProjectId(null);
        setNewProjectName('');
        setDragOrderChanged(false);
        setManageError('');
        loadProjects();
    };

    const closeManageModal = () => {
        setIsManageModalOpen(false);
        setManageProjects([]);
        setEditingProjectId(null);
        setDeletingProjectId(null);
        setNewProjectName('');
        setDragOrderChanged(false);
        setManageError('');
    };

    const handleSaveEdit = async (projectId: number) => {
        if (!editingName.trim()) return;
        setEditSaving(true);
        setManageError('');
        try {
            await apiClient.put(`/project/${projectId}`, { projectName: editingName.trim() });
            setManageProjects(prev => prev.map(p =>
                p.projectId === projectId ? { ...p, projectName: editingName.trim() } : p
            ));
            setEditingProjectId(null);
            refreshProjects();
        } catch (error) {
            setManageError('Failed to update project name.');
        } finally {
            setEditSaving(false);
        }
    };

    const handleConfirmDelete = async (projectId: number) => {
        setDeleteConfirming(true);
        setManageError('');
        try {
            await apiClient.delete(`/project/${projectId}`, {});
            setManageProjects(prev => prev.filter(p => p.projectId !== projectId));
            setDeletingProjectId(null);
            refreshProjects();
        } catch (error) {
            setManageError('Failed to delete project.');
        } finally {
            setDeleteConfirming(false);
        }
    };

    const handleAddProject = async () => {
        if (!newProjectName.trim()) return;
        setNewProjectAdding(true);
        setManageError('');
        try {
            await apiClient.create('/project', { projectName: newProjectName.trim() });
            setNewProjectName('');
            const response: any = await apiClient.get('/project');
            setManageProjects(response?.data ?? []);
            refreshProjects();
        } catch (error) {
            setManageError('Failed to add project.');
        } finally {
            setNewProjectAdding(false);
        }
    };

    const handleDragStart = (index: number) => {
        dragItem.current = index;
    };

    const handleDragEnter = (index: number) => {
        dragOverItem.current = index;
    };

    const handleDrop = () => {
        if (dragItem.current === null || dragOverItem.current === null || dragItem.current === dragOverItem.current) return;
        const items = [...manageProjects];
        const dragged = items.splice(dragItem.current, 1)[0];
        items.splice(dragOverItem.current, 0, dragged);
        dragItem.current = null;
        dragOverItem.current = null;
        setManageProjects(items);
        setDragOrderChanged(true);
    };

    const handleSaveOrder = async () => {
        setSavingOrder(true);
        setManageError('');
        try {
            const updates = manageProjects.map((p, i) => ({ projectId: p.projectId, sortOrder: i + 1 }));
            await apiClient.put('/project/sort-orders', updates);
            setDragOrderChanged(false);
            refreshProjects();
        } catch (error) {
            setManageError('Failed to save order.');
        } finally {
            setSavingOrder(false);
        }
    };

    const handleQuickAction = (actionId: string) => {
        if (actionId === PROJECTS_ACTION_ID) {
            openManageModal();
        }
    };

    const handleQuickActionClick = (event: React.MouseEvent<HTMLButtonElement>, actionId: string) => {
        event.preventDefault();
        event.stopPropagation();
        handleQuickAction(actionId);
    };

    //vertical and semibox resize events
    const resizeSidebarMenu = useCallback(() => {
        var windowSize = document.documentElement.clientWidth;
        const humberIcon = document.querySelector(".hamburger-icon") as HTMLElement;
        var hamburgerIcon = document.querySelector(".hamburger-icon");
        if (windowSize >= 1025) {
            if (document.documentElement.getAttribute("data-layout") === "vertical") {
                document.documentElement.setAttribute("data-sidebar-size", leftsidbarSizeType);
            }
            if (document.documentElement.getAttribute("data-layout") === "semibox") {
                document.documentElement.setAttribute("data-sidebar-size", leftsidbarSizeType);
            }
            if ((sidebarVisibilitytype === "show" || layoutType === "vertical" || layoutType === "twocolumn") && document.querySelector(".hamburger-icon")) {
                if (hamburgerIcon !== null) {
                    hamburgerIcon.classList.remove("open");
                }
            } else {
                if (hamburgerIcon !== null) {
                    hamburgerIcon.classList.add("open");
                }
            }

        } else if (windowSize < 1025 && windowSize > 767) {
            document.body.classList.remove("twocolumn-panel");
            if (document.documentElement.getAttribute("data-layout") === "vertical") {
                document.documentElement.setAttribute("data-sidebar-size", "sm");
            }
            if (document.documentElement.getAttribute("data-layout") === "semibox") {
                document.documentElement.setAttribute("data-sidebar-size", "sm");
            }
            if (humberIcon) {
                humberIcon.classList.add("open");
            }
        } else if (windowSize <= 767) {
            document.body.classList.remove("vertical-sidebar-enable");
            if (document.documentElement.getAttribute("data-layout") !== "horizontal") {
                document.documentElement.setAttribute("data-sidebar-size", "lg");
            }
            if (humberIcon) {
                humberIcon.classList.add("open");
            }
        }
    }, [leftsidbarSizeType, sidebarVisibilitytype, layoutType]);

    useEffect(() => {
        window.addEventListener("resize", resizeSidebarMenu, true);
    }, [resizeSidebarMenu]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        const initMenu = () => {
            const pathName = process.env.PUBLIC_URL + path;
            const ul = document.getElementById("navbar-nav") as HTMLElement;
            const items : any = ul.getElementsByTagName("a");
            let itemsArray = [...items]; // converts NodeList to Array
            removeActivation(itemsArray);
            let matchingMenuItem = itemsArray.find((x) => {
                return x.pathname === pathName;
            });
            if (matchingMenuItem) {
                activateParentDropdown(matchingMenuItem);
            }
        };
        if (props.layoutType === "vertical") {
            initMenu();
        }
    }, [path, props.layoutType]);

    function activateParentDropdown(item : any) {
        item.classList.add("active");
        let parentCollapseDiv = item.closest(".collapse.menu-dropdown");

        if (parentCollapseDiv) {
            // to set aria expand true remaining
            parentCollapseDiv.classList.add("show");
            parentCollapseDiv.parentElement.children[0].classList.add("active");
            parentCollapseDiv.parentElement.children[0].setAttribute("aria-expanded", "true");
            if (parentCollapseDiv.parentElement.closest(".collapse.menu-dropdown")) {
                parentCollapseDiv.parentElement.closest(".collapse").classList.add("show");
                if (parentCollapseDiv.parentElement.closest(".collapse").previousElementSibling)
                    parentCollapseDiv.parentElement.closest(".collapse").previousElementSibling.classList.add("active");
                if (parentCollapseDiv.parentElement.closest(".collapse").previousElementSibling.closest(".collapse")) {
                    parentCollapseDiv.parentElement.closest(".collapse").previousElementSibling.closest(".collapse").classList.add("show");
                    parentCollapseDiv.parentElement.closest(".collapse").previousElementSibling.closest(".collapse").previousElementSibling.classList.add("active");
                }
            }
            return false;
        }
        return false;
    }

    const removeActivation = (items :any) => {
        let actiItems = items.filter((x : any) => x.classList.contains("active"));

        actiItems.forEach((item : any) => {
            if (item.classList.contains("menu-link")) {
                if (!item.classList.contains("active")) {
                    item.setAttribute("aria-expanded", false);
                }
                if (item.nextElementSibling) {
                    item.nextElementSibling.classList.remove("show");
                }
            }
            if (item.classList.contains("nav-link")) {
                if (item.nextElementSibling) {
                    item.nextElementSibling.classList.remove("show");
                }
                item.setAttribute("aria-expanded", false);
            }
            item.classList.remove("active");
        });
    };

    return (
        <React.Fragment>
            {/* menu Items */}
            {(navData || []).map((item : any, key : number) => {
                return (
                    <React.Fragment key={key}>
                        {/* Main Header */}
                        {item['isHeader'] ?
                            <li className={`menu-title${item.actionId ? ' menu-title-with-action' : ''}`}>
                                <span data-key="t-menu">{props.t(item.label)}</span>
                                {item.actionId ? (
                                    <button
                                        type="button"
                                        className="btn btn-ghost-primary btn-sm menu-title-action"
                                        onClick={(event: React.MouseEvent<HTMLButtonElement>) => handleQuickActionClick(event, item.actionId)}
                                        aria-label={props.t("Manage projects")}
                                    >
                                        <i className={item.actionIcon || 'ri-settings-3-line'}></i>
                                    </button>
                                ) : null}
                            </li>
                            : (
                                (item.subItems ? (
                                    <li
                                        className={`nav-item${item.actionId ? ' position-relative menu-item-action' : ''}`}
                                        id={item.id}
                                    >
                                        <Link
                                            onClick={item.click}
                                            className={`nav-link menu-link${item.actionId ? ' pe-5' : ''}`}
                                            to={item.link ? item.link : "/#"}
                                            data-bs-toggle="collapse"
                                        >
                                            <i className={item.icon}></i>
                                            <span data-key="t-apps">{props.t(item.label)}</span>
                                            {item.badgeName ?
                                                <span className={"badge badge-pill bg-" + item.badgeColor} data-key="t-new">{item.badgeName}</span>
                                                : null}
                                        </Link>
                                        {item.actionId ? (
                                            <button
                                                type="button"
                                                className="btn btn-ghost-primary btn-icon btn-sm position-absolute top-50 translate-middle-y end-0 me-2"
                                                onClick={(event) => handleQuickActionClick(event, item.actionId)}
                                                aria-label="Manage projects"
                                            >
                                                <i className={item.actionIcon || 'ri-settings-3-line'}></i>
                                            </button>
                                        ) : null}
                                        <Collapse
                                            className="menu-dropdown"
                                            isOpen={item.stateVariables}
                                            id={`sidebar${item.id}`}>
                                            <ul className="nav nav-sm flex-column test">
                                                {/* subItms  */}
                                                {item.subItems && ((item.subItems || []).map((subItem : any, key : number) => (
                                                        <React.Fragment key={key}>
                                                            {!subItem.isChildItem ? (
                                                                <li className="nav-item">
                                                                    <Link
                                                                        to={subItem.link ? subItem.link : "/#"}
                                                                        className="nav-link"
                                                                    >
                                                                        {props.t(subItem.label)}
                                                                        {subItem.badgeName ?
                                                                            <span className={"badge badge-pill bg-" + subItem.badgeColor} data-key="t-new">{subItem.badgeName}</span>
                                                                            : null}
                                                                    </Link>
                                                                </li>
                                                            ) : (
                                                                <li className="nav-item">
                                                                    <Link
                                                                        onClick={subItem.click}
                                                                        className="nav-link"
                                                                        to="/#"
                                                                        data-bs-toggle="collapse"
                                                                    >
                                                                        {props.t(subItem.label)}
                                                                        {subItem.badgeName ?
                                                                            <span className={"badge badge-pill bg-" + subItem.badgeColor} data-key="t-new">{subItem.badgeName}</span>
                                                                            : null}
                                                                    </Link>
                                                                    <Collapse className="menu-dropdown" isOpen={subItem.stateVariables} id={`sidebar${subItem.id}`}>
                                                                        <ul className="nav nav-sm flex-column">
                                                                            {/* child subItms  */}
                                                                            {subItem.childItems && (
                                                                                (subItem.childItems || []).map((childItem : any, key : number) => (
                                                                                    <React.Fragment key={key}>
                                                                                        {!childItem.childItems ?
                                                                                            <li className="nav-item">
                                                                                                <Link
                                                                                                    to={childItem.link ? childItem.link : "/#"}
                                                                                                    className="nav-link">
                                                                                                    {props.t(childItem.label)}
                                                                                                </Link>
                                                                                            </li>
                                                                                            : <li className="nav-item">
                                                                                                <Link to="/#" className="nav-link" onClick={childItem.click} data-bs-toggle="collapse">
                                                                                                    {props.t(childItem.label)}
                                                                                                </Link>
                                                                                                <Collapse className="menu-dropdown" isOpen={childItem.stateVariables} id={`sidebar${childItem.id}`}>
                                                                                                    <ul className="nav nav-sm flex-column">
                                                                                                        {childItem.childItems.map((subChildItem : any, key : number) => (
                                                                                                            <li className="nav-item" key={key}>
                                                                                                                <Link to={subChildItem.link} className="nav-link" data-key="t-basic-action">{props.t(subChildItem.label)} </Link>
                                                                                                            </li>
                                                                                                        ))}
                                                                                                    </ul>
                                                                                                </Collapse>
                                                                                            </li>
                                                                                        }
                                                                                    </React.Fragment>
                                                                                ))
                                                                            )}
                                                                        </ul>
                                                                    </Collapse>
                                                                </li>
                                                            )}
                                                        </React.Fragment>
                                                    ))
                                                )}
                                            </ul>

                                        </Collapse>
                                    </li>
                                ) : (
                                    <li className={`nav-item${item.actionId ? ' position-relative menu-item-action' : ''}`} id={item.id}>
                                        <Link
                                            className={`nav-link menu-link${item.actionId ? ' pe-5' : ''}`}
                                            to={item.link ? item.link : "/#"}>
                                            <i className={item.icon}></i> <span>{props.t(item.label)}</span>
                                            {item.badgeName ?
                                                <span className={"badge badge-pill bg-" + item.badgeColor} data-key="t-new">{item.badgeName}</span>
                                                : null}
                                        </Link>
                                        {item.actionId ? (
                                            <button
                                                type="button"
                                                className="btn btn-ghost-primary btn-icon btn-sm position-absolute top-50 translate-middle-y end-0 me-2"
                                                onClick={(event) => handleQuickActionClick(event, item.actionId)}
                                                aria-label="Manage projects"
                                            >
                                                <i className={item.actionIcon || 'ri-settings-3-line'}></i>
                                            </button>
                                        ) : null}
                                    </li>
                                ))
                            )
                        }
                    </React.Fragment>
                );
            })}

            {/* Manage Projects Modal */}
            <Modal isOpen={isManageModalOpen} toggle={closeManageModal} centered size="md">
                <ModalHeader toggle={closeManageModal}>Manage Projects</ModalHeader>
                <ModalBody>
                    {manageLoading ? (
                        <div className="text-center py-3 text-muted">Loading projects...</div>
                    ) : (
                        <div>
                            {manageProjects.length === 0 && !manageLoading && (
                                <div className="text-center py-3 text-muted fs-13">No projects yet. Add one below.</div>
                            )}
                            {manageProjects.map((project, index) => (
                                <div
                                    key={project.projectId}
                                    className="d-flex align-items-center gap-2 py-2 border-bottom"
                                    draggable
                                    onDragStart={() => handleDragStart(index)}
                                    onDragEnter={() => handleDragEnter(index)}
                                    onDragEnd={handleDrop}
                                    onDragOver={(e) => e.preventDefault()}
                                >
                                    <i className="ri-drag-move-line text-muted fs-16" style={{ cursor: 'grab' }}></i>

                                    {editingProjectId === project.projectId ? (
                                        <>
                                            <Input
                                                bsSize="sm"
                                                value={editingName}
                                                onChange={(e) => setEditingName(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') handleSaveEdit(project.projectId);
                                                    if (e.key === 'Escape') setEditingProjectId(null);
                                                }}
                                                className="flex-grow-1"
                                                autoFocus
                                            />
                                            <button
                                                className="btn btn-sm btn-ghost-success btn-icon"
                                                onClick={() => handleSaveEdit(project.projectId)}
                                                disabled={editSaving}
                                                title="Save"
                                            >
                                                <i className="ri-check-line"></i>
                                            </button>
                                            <button
                                                className="btn btn-sm btn-ghost-secondary btn-icon"
                                                onClick={() => setEditingProjectId(null)}
                                                disabled={editSaving}
                                                title="Cancel"
                                            >
                                                <i className="ri-close-line"></i>
                                            </button>
                                        </>
                                    ) : deletingProjectId === project.projectId ? (
                                        <div className="d-flex align-items-center gap-2 flex-grow-1">
                                            <span className="text-danger fs-13 flex-grow-1">Delete "{project.projectName}"?</span>
                                            <button
                                                className="btn btn-sm btn-danger"
                                                onClick={() => handleConfirmDelete(project.projectId)}
                                                disabled={deleteConfirming}
                                            >
                                                {deleteConfirming ? '...' : 'Yes'}
                                            </button>
                                            <button
                                                className="btn btn-sm btn-light"
                                                onClick={() => setDeletingProjectId(null)}
                                                disabled={deleteConfirming}
                                            >
                                                No
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            <span className="flex-grow-1 fs-14">{project.projectName}</span>
                                            <button
                                                className="btn btn-sm btn-ghost-primary btn-icon"
                                                onClick={() => {
                                                    setEditingProjectId(project.projectId);
                                                    setEditingName(project.projectName);
                                                    setDeletingProjectId(null);
                                                }}
                                                title="Edit name"
                                            >
                                                <i className="ri-pencil-line"></i>
                                            </button>
                                            <button
                                                className="btn btn-sm btn-ghost-danger btn-icon"
                                                onClick={() => {
                                                    setDeletingProjectId(project.projectId);
                                                    setEditingProjectId(null);
                                                }}
                                                title="Delete project"
                                            >
                                                <i className="ri-delete-bin-line"></i>
                                            </button>
                                        </>
                                    )}
                                </div>
                            ))}

                            {/* Add new project */}
                            <div className="d-flex align-items-center gap-2 mt-3">
                                <Input
                                    placeholder="New project name"
                                    value={newProjectName}
                                    onChange={(e) => setNewProjectName(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddProject(); }}
                                    className="flex-grow-1"
                                />
                                <Button
                                    color="primary"
                                    size="sm"
                                    onClick={handleAddProject}
                                    disabled={newProjectAdding || !newProjectName.trim()}
                                >
                                    {newProjectAdding ? '...' : 'Add'}
                                </Button>
                            </div>
                        </div>
                    )}
                    {manageError && <div className="text-danger mt-2 fs-13">{manageError}</div>}
                </ModalBody>
                {dragOrderChanged && (
                    <ModalFooter>
                        <span className="text-muted fs-13 me-auto">Order changed — save to apply</span>
                        <Button color="primary" onClick={handleSaveOrder} disabled={savingOrder}>
                            {savingOrder ? 'Saving...' : 'Save Order'}
                        </Button>
                    </ModalFooter>
                )}
            </Modal>
        </React.Fragment>
    );
};

VerticalLayout.propTypes = {
    location: PropTypes.object,
    t: PropTypes.any,
};

export default withRouter(withTranslation()(VerticalLayout));
